import React, { useCallback, useState } from 'react';
import Form from 'react-jsonschema-form';
import { Button, Input } from 'reactstrap';
import ReactDOM from 'react-dom';
import axios from 'axios';
import moment from 'moment';
import { SingleDatePicker } from 'react-dates';
import 'react-dates/initialize';

import MeterPointSelector from '../../../MeterPointSelector/MeterPointSelector';
import SSCIdSelector from '../../../SSCIdSelector/SSCIdSelector';
import { changeOfMeteringSchema, changeOfMeteringUISchema } from '../../schemas/changeOfMeteringSchema';
import { useProcessForm } from '../../hooks/useProcessForm';
import ExpiringAlert from '../../../ExpiringAlert/ExpiringAlert';

// Time Picker Widget - converts between HH:mm display and HHmmss storage format
const TimePickerWidget = ({ value, onChange, placeholder, disabled, required }) => {
  // Convert HHmmss to HH:mm for display
  const formatForDisplay = (hhmmss) => {
    if (!hhmmss || hhmmss.length !== 6) return '';
    const hours = hhmmss.substring(0, 2);
    const minutes = hhmmss.substring(2, 4);
    return `${hours}:${minutes}`;
  };

  // Convert HH:mm to HHmmss for storage
  const formatForStorage = (hhmm) => {
    if (!hhmm) return '';
    const [hours, minutes] = hhmm.split(':');
    return `${hours}${minutes}00`; // Append 00 for seconds
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange(formatForStorage(newValue));
  };

  return (
    <Input
      type='time'
      value={formatForDisplay(value)}
      onChange={handleChange}
      disabled={disabled}
      required={required}
      placeholder={placeholder}
      className='form-control'
    />
  );
};

// Date Picker Widget - uses SingleDatePicker and converts to yyyyMMdd storage format
const DatePickerWidget = ({ value, onChange, disabled }) => {
  const [focused, setFocused] = useState(false);

  // Convert yyyyMMdd to moment for display
  const getMomentValue = () => {
    if (!value || value.length !== 8) return null;
    return moment(value, 'YYYYMMDD');
  };

  // Convert moment to yyyyMMdd for storage
  const handleDateChange = (date) => {
    if (date) {
      onChange(date.format('YYYYMMDD'));
    } else {
      onChange('');
    }
  };

  // Only allow today or future dates
  const isOutsideRange = (day) => {
    return day.isBefore(moment().startOf('day'));
  };

  return (
    <SingleDatePicker
      date={getMomentValue()}
      onDateChange={handleDateChange}
      focused={focused}
      onFocusChange={({ focused }) => setFocused(focused)}
      numberOfMonths={1}
      isOutsideRange={isOutsideRange}
      displayFormat='DD/MM/YYYY'
      placeholder='Select date'
      disabled={disabled}
      id='appointment-date-picker'
      block
    />
  );
};

// Define widget wrappers at module level to prevent remounting on form changes
const MpanCoreField = (props) => (
  <div className='form-group field'>
    <label className='control-label' htmlFor='root_mpan_core'>
      MPAN Core<span className='required'>*</span>
    </label>
    <MeterPointSelector
      value={props.formData}
      onChange={props.onChange}
      placeholder='Select MPAN'
      includeEnergisationStatus
    />
  </div>
);

const DatePickerWidgetWrapper = (props) => <DatePickerWidget {...props} />;
const EarliestTimeWidget = (props) => <TimePickerWidget {...props} />;
const LatestTimeWidget = (props) => <TimePickerWidget {...props} />;
const SSCIdWidget = (props) => <SSCIdSelector {...props} placeholder='Select SSC ID' />;

// UI Schema with widgets - defined at module level
const uiSchemaWithWidgets = {
  ...changeOfMeteringUISchema,
  mpan_core: {
    ...changeOfMeteringUISchema.mpan_core,
    'ui:field': MpanCoreField,
  },
  appointment_date: {
    ...changeOfMeteringUISchema.appointment_date,
    'ui:widget': DatePickerWidgetWrapper,
  },
  earliest_appointment_time: {
    ...changeOfMeteringUISchema.earliest_appointment_time,
    'ui:widget': EarliestTimeWidget,
  },
  latest_appointment_time: {
    ...changeOfMeteringUISchema.latest_appointment_time,
    'ui:widget': LatestTimeWidget,
  },
  ssc_id: {
    ...changeOfMeteringUISchema.ssc_id,
    'ui:widget': SSCIdWidget,
  },
};

const ChangeOfMeteringForm = ({ onClose, initialData }) => {
  const initial = initialData && initialData.mpan_core
    ? { ...initialData, mpan_core: { name: initialData.mpan_core } }
    : initialData || {};
  const { formContext, updateFormContext, isLoading, setIsLoading } = useProcessForm(initial);

  const handleChange = useCallback(
    ({ formData }) => {
      // Extract energisation status from mpan_core when it's an object
      const mpanCoreValue = formData.mpan_core;
      if (mpanCoreValue && typeof mpanCoreValue === 'object' && mpanCoreValue.energisationStatus) {
        updateFormContext({
          ...formData,
          requested_energisation_status: mpanCoreValue.energisationStatus,
        });
      } else {
        updateFormContext(formData);
      }
    },
    [updateFormContext],
  );

  // Validation functions
  const isDateTodayOrFuture = (dateStr) => {
    if (!dateStr || dateStr.length !== 8) return false;

    const year = parseInt(dateStr.substring(0, 4), 10);
    const month = parseInt(dateStr.substring(4, 6), 10);
    const day = parseInt(dateStr.substring(6, 8), 10);

    const inputDate = new Date(year, month - 1, day);
    inputDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return inputDate >= today;
  };

  const isValidTimeFormat = (timeStr) => {
    if (!timeStr || timeStr.length !== 6) return false;
    const regex = /^\d{6}$/;
    if (!regex.test(timeStr)) return false;

    const hours = parseInt(timeStr.substring(0, 2), 10);
    const minutes = parseInt(timeStr.substring(2, 4), 10);
    const seconds = parseInt(timeStr.substring(4, 6), 10);

    return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59 && seconds >= 0 && seconds <= 59;
  };

  const transformErrors = useCallback((errors) => {
    return errors.map((error) => {
      switch (error.property) {
        case '.mpan_core':
          return { ...error, message: 'MPAN Core is a required property' };
        case '.appointment_date':
          return { ...error, message: 'Appointment Date is a required property' };
        case '.earliest_appointment_time':
          return { ...error, message: 'Earliest Appointment Time is a required property' };
        case '.latest_appointment_time':
          return { ...error, message: 'Latest Appointment Time is a required property' };
        case '.remove_all_meters':
          return { ...error, message: 'Remove All Meters is a required property' };
        case '.retrieval_method':
          return { ...error, message: 'Retrieval Method is a required property' };
        default:
          return error;
      }
    });
  }, []);

  const validateForm = (formData) => {
    const errors = [];

    // Validate appointment_date - with date picker, format is always valid if present
    if (formData.appointment_date) {
      if (!isDateTodayOrFuture(formData.appointment_date)) {
        errors.push('Appointment Date must be today or in the future');
      }
    }

    // Validate earliest_appointment_time format
    if (formData.earliest_appointment_time && !isValidTimeFormat(formData.earliest_appointment_time)) {
      errors.push('Earliest Appointment Time is invalid');
    }

    // Validate latest_appointment_time format
    if (formData.latest_appointment_time && !isValidTimeFormat(formData.latest_appointment_time)) {
      errors.push('Latest Appointment Time is invalid');
    }

    return errors;
  };

  const transformFormDataToPayload = (formData) => {
    const payload = {
      mpan_core: parseInt(formData.mpan_core.name, 10),
      appointment_date: formData.appointment_date,
      earliest_appointment_time: formData.earliest_appointment_time,
      latest_appointment_time: formData.latest_appointment_time,
      requested_energisation_status: formData.requested_energisation_status,
      remove_all_meters: formData.remove_all_meters,
      retrieval_method: formData.retrieval_method,
    };

    // Add optional fields if provided
    if (formData.additional_information && formData.additional_information.trim()) {
      payload.additional_information = formData.additional_information.trim();
    }

    if (formData.ssc_id && formData.ssc_id.trim()) {
      payload.ssc_id = formData.ssc_id.trim();
    }

    // Send as null
    payload.metering_system_nsfc = null;
    payload.communications_method = null;

    return payload;
  };

  const loadingIcon = isLoading ? (
    <span>
      <i className='fa fa-circle-o-notch fa-spin'></i>{' '}
    </span>
  ) : null;

  return (
    <div className='change-of-metering-form'>
      <Form
        schema={changeOfMeteringSchema}
        uiSchema={uiSchemaWithWidgets}
        formContext={formContext}
        formData={formContext}
        transformErrors={transformErrors}
        showErrorList={false}
        onSubmit={({ formData }) => {
          // Custom validation
          const validationErrors = validateForm(formData);
          if (validationErrors.length > 0) {
            ReactDOM.render(
              <ExpiringAlert color='danger' message={validationErrors.join('. ')} />,
              document.getElementById('alert').appendChild(document.createElement('div')),
            );
            return;
          }

          // Check if energisation status is set
          if (!formData.requested_energisation_status) {
            ReactDOM.render(
              <ExpiringAlert
                color='danger'
                message='Please select a meter point to auto-fill the Requested Energisation Status'
              />,
              document.getElementById('alert').appendChild(document.createElement('div')),
            );
            return;
          }

          setIsLoading(true);
          const payload = transformFormDataToPayload(formData);

          axios
            .post('/publishMessage', payload, {
              headers: {
                Authorization: `Bearer ${localStorage.token}`,
              },
              params: { queue: 'BOP.Dataflows.StartMeteringChange' },
            })
            .then((response) => {
              setIsLoading(false);
              if (response.data === 'Message sent') {
                ReactDOM.render(
                  <ExpiringAlert color='success' message='Change of Metering request submitted successfully!' />,
                  document.getElementById('alert').appendChild(document.createElement('div')),
                );
                onClose();
              } else {
                ReactDOM.render(
                  <ExpiringAlert color='danger' message={response.data.message || 'Error submitting request'} />,
                  document.getElementById('alert').appendChild(document.createElement('div')),
                );
              }
            })
            .catch((error) => {
              setIsLoading(false);
              // eslint-disable-next-line no-console
              console.error('Error submitting change of metering request:', error);
              ReactDOM.render(
                <ExpiringAlert color='danger' message='Error submitting request. Request logged to console.' />,
                document.getElementById('alert').appendChild(document.createElement('div')),
              );
            });
        }}
        onChange={handleChange}
      >
        <div>
          <Button type='submit' disabled={isLoading} color='primary'>
            {isLoading && loadingIcon}Submit Request
          </Button>
          &nbsp;&nbsp;&nbsp;
          <Button type='button' onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default ChangeOfMeteringForm;
